import { useContext, useEffect, useMemo, useState } from 'react'
import { __ } from '@wordpress/i18n'
import Button from '../../Common/Button';
import Source from '../../Common/Source';
import ModalContainer from '../../Global/ModalContainer';
import AddSourceModal from '../../Modals/AddSourceModal';
import EditWooProductsModal from '../../Modals/EditWooProductsModal';
import EditEddDownloadsModal from '../../Modals/EditEddDownloadsModal';
import SbUtils from '../../../Utils/SbUtils';
import FreeRetrieverModal from '../../Modals/FreeRetrieverModal';
import Pagination from '../../Common/Pagination';
import {
    isMultiProductSource,
    getSourceBadge,
    getSourceProducts,
    getSourceCategories,
    getSourceTags
} from '../../../Utils/WooCommerceSourceUtils';
import {
    isMultiDownloadSource as isMultiDownloadEddSource,
    getSourceBadge as getEddSourceBadge,
    getSourceDownloads as getEddSourceDownloads,
    getSourceCategories as getEddSourceCategories,
    getSourceTags as getEddSourceTags
} from '../../../Utils/EddSourceUtils';

const SelectSourcesSection = ( props ) => {

    const {
        sourcesCount,
        sources,
        fbModal,
        editorNotification,
        sbCustomizer,
        editorTopLoader,
        addSModal,
        freeRet,
        freeRetModal,
        bulkSr,
        apis
    } = useContext( SbUtils.getCurrentContext() );

    const [ addSourcesModalActive, setAddSourcesModalActive ] = useState( false );
    const [ collapsedSources, setCollapsedSources ] = useState( [] );
    const [ isLoadingSources, setIsLoadingSources ] = useState( false );
    const [ sourcesPage, setSourcesPage ] = useState(1);
    const [ editingSource, setEditingSource ] = useState( null );
    const [ editingEddSource, setEditingEddSource ] = useState( null );
    // Constant for sources per page (matches backend SOURCES_PER_PAGE)
    const SOURCES_PER_PAGE = 20;
    const initialTotalPages = Math.ceil((sourcesCount.sourcesNumber || 0) / SOURCES_PER_PAGE) || 1;
    const [ totalPages, setTotalPages ] = useState(initialTotalPages);


    useEffect(() => {
        if (sbCustomizer?.openSourceModal) {
            setAddSourcesModalActive( true )
        }
        const initModalScreen = SbUtils.initAddSourceModalScreen(apis, freeRet)  // addSource, freeRetriever
        addSModal.setModalType(initModalScreen)

        const initRetrModal = SbUtils.initRetrieveModalScreen(apis, freeRet) // verifyEmail - sourceAdded - limitExceeded - addApiKey
        freeRetModal.setRetModalType(initRetrModal)
    }, []);

    // Recalculate totalPages when sourcesCount changes (e.g., after adding/removing sources)
    useEffect(() => {
        const newTotalPages = Math.ceil((sourcesCount.sourcesNumber || 0) / SOURCES_PER_PAGE) || 1;
        setTotalPages(newTotalPages);
    }, [sourcesCount.sourcesNumber]);


    const connectFacebookActiveMemo = useMemo( () => {
        if( fbModal.connectFacebookActive === true){
            setAddSourcesModalActive( true )
            return true;
        }
        return false;
    },[ fbModal ]);

    const addSourcePopup = () => {
        const initModalScreen = SbUtils.initAddSourceModalScreen(apis, freeRet)  // addSource, freeRetriever
        addSModal.setModalType(initModalScreen)
        setAddSourcesModalActive( true )
    }

    const fetchSources = (page) => {
        setIsLoadingSources(true);
        const formData = {
            action : 'sbr_feed_saver_manager_load_more_sources',
            sources_page : page
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( resp ) => {
                setIsLoadingSources(false);
                if( resp?.sourcesList !== undefined ){
                    sources.setSourcesList(resp?.sourcesList);
                    sourcesCount.setSourcesNumber( resp?.sourcesCount );
                    setTotalPages( resp?.totalPages || 1 );
                }
            },
            editorTopLoader,
            null,
            null
        );
    }

    const handlePageChange = (page) => {
        setSourcesPage(page);
        fetchSources(page);
    }


    const checkSourceHistoryFailure = (source) => {
        if (!['google','yelp'].includes(source?.provider)) {
            return false;
        }
        const sourceAccountID = source?.account_id;
        if (bulkSr?.bulkHistorySources[sourceAccountID] !== undefined) {
            return bulkSr?.bulkHistorySources[sourceAccountID]?.retry === true && bulkSr?.bulkHistorySources[sourceAccountID]?.is_done === false;
        }
        return false;
    }



    const [onSuccessApiKey, setOnSuccessApiKey] = useState(null)
    return (
        <>
            <div
                className={'sb-'+props.context+'-content sb-fs'}
                data-context={ props.context }
            >
                {
                    sources.sourcesList.length !== 0 &&
                    <div className='sb-sources-list sb-fs'>
                        <Button
                            text={ __( 'Add New', 'sb-customizer' ) }
                            icon='plus'
                            customClass='sb-addnew-source'
                            boxshadow={ false }
                            size='small'
                            iconSize='14'
                            onClick={ () => {
                                addSourcePopup()
                            } }
                        />
                        {
                            sources.sourcesList.map( ( source, sourceInd ) => {
                                const isMultiProductWoo = isMultiProductSource( source );
                                const isMultiDownloadEdd = isMultiDownloadEddSource( source );
                                const isWooSource = source.provider === 'woocommerce';
                                const isEddSource = source.provider === 'edd';

                                // Get badge and info based on provider
                                const badge = isEddSource ? getEddSourceBadge( source ) : getSourceBadge( source );
                                const productsInfo = isEddSource ? getEddSourceDownloads( source ) : getSourceProducts( source );
                                const categoriesInfo = isEddSource ? getEddSourceCategories( source ) : getSourceCategories( source );
                                const tagsInfo = isEddSource ? getEddSourceTags( source ) : getSourceTags( source );

                                // Determine edit click handler based on provider
                                let onEditClick = undefined;
                                if ( isMultiProductWoo ) {
                                    onEditClick = () => setEditingSource( source );
                                } else if ( isMultiDownloadEdd ) {
                                    onEditClick = () => setEditingEddSource( source );
                                }

                                return (
                                    <Source
                                        key={ sourceInd }
                                        provider={ source.provider }
                                        checkbox={props.context !== 'settingspage' && true}
                                        isChecked={props.context !== 'settingspage' && props.selectedSources.includes( source.account_id ) }
                                        name={ source.name }
                                        info={ source.info }
                                        badge={ badge }
                                        productsInfo={ productsInfo }
                                        categoriesInfo={ categoriesInfo }
                                        tagsInfo={ tagsInfo }
                                        removeIcon={ props.context === 'settingspage' }
                                        infoIcon={ props.context === 'settingspage' }
                                        accountId={ source.account_id }
                                        editorNotification={ editorNotification }
                                        isCollapsed={ collapsedSources.includes( source.account_id )  }
                                        needHistoryCheck={ checkSourceHistoryFailure( source ) }
                                        onRemoveClick={ () => {
                                            props.context === 'settingspage' &&
                                                props.deleteSource( source );
                                        }}
                                        onClick={ () => {
                                            props.context !== 'settingspage' &&
                                                props.selectSourcesAction( source.account_id );
                                        }}
                                        onShowInfoClick={ () => {
                                            setCollapsedSources( [...SbUtils.updateArray( source.account_id,collapsedSources )] );
                                        }}
                                        onEditClick={ onEditClick }

                                    />
                                )
                            } )
                        }
                    </div>
                }
                {
                    sources.sourcesList.length === 0 &&
                    <div className='sb-emptysources-notice'>
                        <span className='sb-fs sb-text-small sb-dark-text'>{ __( 'Looks like you have not added any source. Use "Add Source" to add a new one.' , 'sb-customizer') }</span>
                        <Button
                            text={ __( 'Add Source', 'sb-customizer' ) }
                            type='secondary'
                            size='medium'
                            icon='plus'
                            boxshadow={ false }
                            iconSize='14'
                            onClick={ () => {
                                addSourcePopup()
                            } }
                        />
                    </div>
                }

                {
                    <div className='sb-sources-bottom sb-fs'>
                        <div className='sb-sources-pagination'>
                            <Pagination
                                currentPage={sourcesPage}
                                totalPages={totalPages}
                                totalItems={sourcesCount.sourcesNumber}
                                onPageChange={handlePageChange}
                                isLoading={isLoadingSources}
                            />
                        </div>
                    </div>
                }
            </div>
            {
                addSourcesModalActive &&
                <ModalContainer
                    size={SbUtils.getAddSourceModalSize(addSModal?.modalType === 'freeRetriever')}
                    closebutton={true}
                    onClose={ () => {
                        if (
                            addSModal?.modalType === 'freeRetriever'
                            && freeRetModal?.retModalType === 'verifyEmail'
                            && !SbUtils.checkAPIKeyExists('google', apis)
                            && !SbUtils.checkAPIKeyExists('yelp', apis)
                        ) {
                            setAddSourcesModalActive( false )
                        } else {
                            if (addSModal?.modalType !== 'freeRetriever'){
                                setAddSourcesModalActive( false )
                            }
                            freeRetModal.setRetModalType(SbUtils.initRetrieveModalScreen(apis, freeRet))
                            addSModal.setModalType('addSource')
                        }
                        setOnSuccessApiKey(null)

                    } }
                >
                    {
                        addSModal?.modalType === 'addSource' &&
                        <AddSourceModal
                            openDialog={ () => {
                                setAddSourcesModalActive( true )
                            } }
                            onCancel={ () => {
                                setOnSuccessApiKey(null)
                                setAddSourcesModalActive( false )
                            } }
                            onSuccess={ () => {
                                // Refresh sources for current page to preserve pagination
                                fetchSources( sourcesPage );
                            } }
                            onSuccessApiKey={onSuccessApiKey}
                        />
                    }
                    {
                       addSModal?.modalType === 'freeRetriever' &&
                        <FreeRetrieverModal
                            screenType={freeRetModal?.retModalType} // - verifyEmail - sourceAdded - limitExceeded - addApiKey
                            openDialog={ () => {
                                setAddSourcesModalActive( true )
                            } }
                            onCancel={ () => {
                                setAddSourcesModalActive( false )
                                setOnSuccessApiKey(null)
                            } }
                            onSuccessApiKey={(provider) => {
                                setOnSuccessApiKey(provider)
                            }}
                        />
                    }
                </ModalContainer>
            }
            {
                editingSource &&
                <ModalContainer
                    size='medium'
                    closebutton={true}
                    onClose={ () => setEditingSource( null ) }
                >
                    <EditWooProductsModal
                        source={ editingSource }
                        currentPage={ sourcesPage }
                        onCancel={ () => setEditingSource( null ) }
                        onSuccess={ () => {
                            setEditingSource( null );
                            // Refresh sources for current page to stay on same page after edit
                            fetchSources( sourcesPage );
                        } }
                    />
                </ModalContainer>
            }
            {
                editingEddSource &&
                <ModalContainer
                    size='medium'
                    closebutton={true}
                    onClose={ () => setEditingEddSource( null ) }
                >
                    <EditEddDownloadsModal
                        source={ editingEddSource }
                        currentPage={ sourcesPage }
                        onCancel={ () => setEditingEddSource( null ) }
                        onSuccess={ () => {
                            setEditingEddSource( null );
                            // Refresh sources for current page to stay on same page after edit
                            fetchSources( sourcesPage );
                        } }
                    />
                </ModalContainer>
            }
        </>
    )
}

export default SelectSourcesSection;
